/**
 * Thin wrapper around gtag so lead actions become GA4 events.
 * Import each of these as a conversion in Google Ads (Tools → Conversions → Import → GA4).
 */

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

export type LeadEvent =
  | 'generate_lead' // quote or contact form submitted
  | 'click_to_call'
  | 'click_whatsapp'
  | 'click_email'
  | 'quote_cta_click';

export const trackEvent = (name: LeadEvent, params: Record<string, string | number | boolean> = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, { page_path: window.location.pathname, ...params });
};

const CONSENT_KEY = 'behson-consent-v1';
export type ConsentChoice = 'granted' | 'denied';

export const readConsent = (): ConsentChoice | null => {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
};

/** Consent Mode v2: update all four signals together. */
export const applyConsent = (choice: ConsentChoice) => {
  try {
    window.localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    /* private mode: ignore */
  }
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  });
};

/**
 * One delegated listener instead of wiring every phone / WhatsApp / email link by hand.
 * Returns a cleanup function.
 */
export const installLeadClickTracking = () => {
  const handler = (event: MouseEvent) => {
    const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor) return;
    const href = anchor.getAttribute('href') ?? '';
    if (href.startsWith('tel:')) trackEvent('click_to_call', { link_url: href });
    else if (href.includes('wa.me') || href.includes('whatsapp.com')) trackEvent('click_whatsapp', { link_url: href });
    else if (href.startsWith('mailto:')) trackEvent('click_email', { link_url: href });
  };
  document.addEventListener('click', handler);
  return () => document.removeEventListener('click', handler);
};
